import { types } from "mobx-state-tree";
import fetcher from "../utils/fetcher";
import userStore, { Role } from "./userStore";
import { persist } from "../utils/persist";
import { cleanFormError } from "../utils/errorMessage";

const TenantsStore = types
  .model("TenantsStore", {
    contracts: types.frozen(),
    siteContracts: types.frozen(),
    selectedProject: types.frozen(),
    selectedContract: types.frozen(),
    selectedContacts: types.frozen(),
    selectedMessage: types.frozen(),
    selectedDoc: types.frozen(),
    error: types.frozen(),
    sites: types.frozen(),
    selectedTheme: types.frozen(),
    licenses: types.frozen(),
    siteLogo: types.frozen(),
    selectedSiteSpace: types.frozen(),
    allMessages: types.frozen(),
    // True while a call that triggers the Priority ZCOK_TNTPORTALSITES rebuild
    // (get_cust_data) is in flight. The UI shows a blocking overlay so the user
    // can't fire a second overlapping rebuild from the screen.
    busy: types.optional(types.boolean, false),
  })
  .actions((self) => {
    // Ref-count in-flight rebuild-triggering calls so the overlay only clears
    // once the last one finishes (not when the first of several resolves).
    let inFlightCount = 0;

    let customerDataPromise = null;
    let loadedCustEnv = null;

    // The header (unread badge) and the lobby ask for the same contract
    // messages at the same moment. Share the in-flight request per contract so
    // Priority is asked once, without caching anything past the response.
    const messagesPromises = new Map();

    const getSaleContracts = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_sale_contracts();
      if (response?.status === 200 && response?.data) {
        const contracts = response.data;

        self.setContracts(contracts);

        return contracts;
      } else {
        self.setError("error:error_failed_get_sale_contracts_req", response);
      }
    };

    const getSiteSaleContracts = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_site_sale_contracts();
      if (response?.status === 200 && response?.data) {
        const contracts = response.data;
        self.setSiteContracts(contracts);

        return contracts;
      } else {
        self.setError("error:error_failed_get_site_sale_contracts_req", response);
      }
    };

    const getBedekSiteSpaces = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_bedek_site_spaces();
      if (response?.status === 200 && response?.data) {
        const spaces = response.data;

        return spaces;
      } else {
        self.setError("error:error_failed_get_bedek_site_spaces_req", response);
      }
    };

    const getSaleContract = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_sale_contract();
      if (response?.status === 200 && response?.data) {
        const contract = response.data;
        await self.setSelectedContract({
          ...contract,
          SITEID: data?.SITEID,
          DNAME: data?.DNAME,
        });
        return contract;
      } else {
        self.setError("error:error_failed_get_sale_contract_req", response);
      }
    };

    const getSaleContractMessages = async (data) => {
      const contract = data?.CONTNAME;
      if (contract && messagesPromises.has(contract)) {
        return messagesPromises.get(contract);
      }

      self.setError("");
      const request = (async () => {
        try {
          const response = await fetcher()
            .post(data)
            .from.tenants.get_sale_contract_messages();
          if (response?.status === 200 && response?.data) {
            const messages = response.data;
            self.setAllMessages(messages);

            return messages;
          }
          self.setError("error:error_failed_get_sale_contract_req", response);
        } finally {
          if (contract) messagesPromises.delete(contract);
        }
      })();

      if (contract) messagesPromises.set(contract, request);
      return request;
    };
    const getSaleContractDocs = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_sale_contract_docs();
      if (response?.status === 200 && response?.data) {
        const docs = response.data;
        return docs;
      } else {
        self.setError("error:error_failed_get_sale_contract_req", response);
      }
    };

    const getSaleContractApplies = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_sale_contract_applies();
      if (response?.status === 200 && response?.data) {
        const applies = response.data;
        return applies;
      } else {
        self.setError("error:error_failed_get_sale_contract_applias_req", response);
      }
    };

    const getProjectImage = async (data) => {
      self.setError("");
      const imgRes = await fetcher().post(data).from.tenants.get_project_img();
      if (imgRes?.status === 200 && imgRes?.data) {
        const image = imgRes?.data;
        self.setSiteLogo(image);
      } else {
        self.setError("error:error_failed_get_project_img_req", imgRes);
      }
    };

    const getDepartments = async (data) => {
      self.setError("");
      const imgsRes = await fetcher().post(data).from.tenants.get_departments();
      if (imgsRes?.status === 200 && imgsRes?.data) {
        const images = imgsRes?.data;
        return images || [];
      } else {
        self.setError("error:error_failed_get_departments_req", imgsRes);
      }
    };

    const getCustomerSites = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_customer_sites(data);
      if (response?.status === 200 && response?.data) {
        const sites = response.data;

        self.setCustomerSites(sites);
      } else {
        self.setError("error:error_failed_get_customer_sites_req", response);
      }
    };

    const updateCustomerSites = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.update_customer_sites(data);
      if (response?.status === 200 && response?.data?.success) {
        await self.getCustomerSites(data);
      } else {
        self.setError("error:error_failed_update_customer_sites_req", response);
      }
    };

    const updateCustomerUsers = async (data) => {
      self.setError("");
      inFlightCount += 1;
      self.setBusy(true);
      try {
        const response = await fetcher()
          .post(data)
          .from.tenants.update_customer_users(data);
        if (!(response?.status === 200 && response?.data?.success)) {
          self.setError("error:error_failed_update_customer_users_req", response);
        }
      } finally {
        inFlightCount = Math.max(0, inFlightCount - 1);
        if (inFlightCount === 0) self.setBusy(false);
      }
    };

    const getCustomerData = async (data, force = false) => {
      if (customerDataPromise) return customerDataPromise;
      const custEnv = `${data?.MAINCUST ?? data?.CUST}:${data?.MAINENV}`;
      if (!force && self.sites?.length && loadedCustEnv === custEnv) {
        return self.sites;
      }
      self.setError("");
      inFlightCount += 1;
      self.setBusy(true);
      customerDataPromise = (async () => {
        try {
          const response = await fetcher().post(data)
            .from.tenants.get_cust_data(data);
          if (response?.status === 200 && response?.data) {
            self.setCustomerSites(response?.data);
            loadedCustEnv = custEnv;
            return response?.data;
          }
          self.setError("error:error_failed_get_customer_data_req", response);
        } finally {
          inFlightCount = Math.max(0, inFlightCount - 1);
          if (inFlightCount === 0) self.setBusy(false);
          customerDataPromise = null;
        }
      })();
      return customerDataPromise;
    };





    const getSaleContractNote = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_sale_contract_note(data);
      if (response?.status === 200 && response?.data) {
        return response.data;
      } else {
        self.setError("error:error_failed_get_sale_contract_note_req", response);
      }
    };

    const addSaleContractNote = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.add_sale_contract_note(data);
      if (response?.status === 200 && response?.data) {
        return response.data;
      } else {
        self.setError(
          "error:error_failed_add_sale_contract_note_req",
          response
        );
      }
    };

    const getPortalData = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_portal_user_data(data);
      if (response?.status === 200 && response?.data) {
        self.setLicenses(response?.data?.SUBLICENSENUM);
      } else {
        self.setError("error:error_failed_get_cportal_data_req", response);
      }
    };

    const updateTenants = async (data) => {
      self.setError("");

      const response = await fetcher()
        .post(data)
        .from.tenants.update_tenants(data);

      if (response?.status === 200 && response?.data) {
        self.setLicenses(response?.data?.SUBLICENSENUM);
      } else {
        self.setError("error:error_failed_update_sites_tenant_req", response);
      }
    };

    const getTenants = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_tenants_in_portal(data);
      if (response?.status === 200 && response?.data) {
        return response?.data;
      } else {
        self.setError("error:error_failed_get_sites_tenant_req", response);
      }
    };

    const getNoteTopics = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_note_topics(data);
      if (response?.status === 200 && response?.data) {
        return response?.data;
      } else {
        self.setError("error:error_failed_get_note_topics_req", response);
      }
    };

    const getNoteStatuses = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.get_note_statuses(data);
      if (response?.status === 200 && response?.data) {
        return response?.data;
      } else {
        self.setError("error:error_failed_get_note_statuses_req", response);
      }
    };

    const updateMassge = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.update_message(data);
      if (response?.status === 200 && response?.data?.success) {
      } else {
        self.setError("error:error_failed_update_message_req", response);
      }
    };

    const updateNote = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.update_note(data);
      if (response?.status === 200 && response?.data?.success) {
      } else {
        self.setError("error:error_failed_update_note_req", response);
      }
    };

    const updateSiteActivation = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.update_site(data);
      if (response?.status === 200 && response?.data) {
        await self.getCustomerData(data, true);
        self.setSelectedProject(response?.data);
      } else {
        self.setError("error:error_failed_update_site_activation_req", response);
      }
    };



    const updateCustSiteActivation = async (data) => {
      self.setError("");
      inFlightCount += 1;
      self.setBusy(true);
      try {
        const response = await fetcher()
          .post(data)
          .from.tenants.update_cust_site(data);
        if (response?.status === 200 && response?.data) {
          await self.getCustomerData(data, true);
          self.setSelectedProject(response?.data);
        } else {
          self.setError("error:error_failed_update_site_activation_req", response);
        }
      } finally {
        inFlightCount = Math.max(0, inFlightCount - 1);
        if (inFlightCount === 0) self.setBusy(false);
      }
    };


    const updateSpaceActivation = async (data) => {
      self.setError("");
      inFlightCount += 1;
      self.setBusy(true);
      try {
        const response = await fetcher()
          .post(data)
          .from.tenants.update_cust_site_space(data);
        if (response?.status === 200 && response?.data) {
          await getSiteSaleContracts(data);
          await getCustomerData(data, true);
        } else {
          self.setError(
            "error:error_failed_update_tenant_activation_req",
            response
          );
        }
      } finally {
        inFlightCount = Math.max(0, inFlightCount - 1);
        if (inFlightCount === 0) self.setBusy(false);
      }
    };


    const updateTenantActivation = async (data) => {
      self.setError("");
      inFlightCount += 1;
      self.setBusy(true);
      try {
        const response = await fetcher()
          .post(data)
          .from.tenants.update_cust_site(data);
        if (response?.status === 200 && response?.data) {
          await getSiteSaleContracts(data);
          await getCustomerData(data, true);
        } else {
          self.setError(
            "error:error_failed_update_tenant_activation_req",
            response
          );
        }
      } finally {
        inFlightCount = Math.max(0, inFlightCount - 1);
        if (inFlightCount === 0) self.setBusy(false);
      }
    };

    const setSelectedProject = (project) => {
      self.selectedProject = project;
      self.setSelectedTheme(project?.THEME);
    };

    const setSelectedContacts = (contacts) => {
      self.selectedContacts = contacts;
    };

    const setSiteLogo = (image) => {
      self.siteLogo = image;
    };

    function setSelectedTheme(theme) {
      self.selectedTheme = theme;
    }

    const setContracts = (contracts) => {
      self.contracts = contracts;
    };

    const setCustomerSites = (sites) => {
      self.sites = sites;
    };



    const setSiteContracts = (contracts) => {
      self.siteContracts = contracts;
    };

    const setLicenses = (num) => {
      self.licenses = num;
    };

    const setSelectedContract = async (contract) => {
      self.selectedContract = contract;


    };

    async function sendResetPassword(data) {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.send_reset_password();

      if (response?.status !== 200) {
        const errorText =
          response?.status === 404
            ? "error:error_failed_user_not_exists"
            : "error:error_failed_reset_password_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function sendLicenseUpdateEmail(data) {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.send_license_update_email();

      if (response?.status !== 200) {
        const errorText = "error:error_failed_send_license_update_email";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function sendFormEmail(data) {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.send_form_email();

      if (response?.status !== 200) {
        const errorText = "error:error_failed_send_license_update_email";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function resetPassword(data) {
      self.setError("");
      const response = await fetcher().post(data).from.tenants.reset_password();

      if (response?.status !== 200) {
        const errorText =
          response.status === 505
            ? "error:error_failed_same_old_pwd"
            : response.status === 404
              ? "error:error_failed_user_not_exists"
              : "error:error_failed_reset_password_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function setAdminPassword(data) {
      self.setError("");
      const response = await fetcher().post(data).from.tenants.set_admin_password();

      if (response?.status !== 200) {
        const errorText =
          response.status === 404
            ? "error:error_failed_user_not_exists"
            : "error:error_failed_reset_password_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      // Store user details from response
      if (response?.data) {
        userStore.setUserDetails({
          ...response.data,
          USERID: response.data.ID,
          MAINCUST: response.data.CUST,
          role: Role.Admin,
        });
      }
      return {
        isSucceed: true,
      };
    }

    const setSelectedDoc = (doc) => {
      self.selectedDoc = doc;
    };

    const setSelectedMessage = (message) => {
      self.selectedMessage = message;
    };

    function setError(error, response) {
      self.error = cleanFormError(response, error);
    }

    const setSelectedSiteSpace = (sitespace) => {
      self.selectedSiteSpace = sitespace;
    };

    const setAllMessages = (messages) => {
      self.allMessages = messages;
    };

    const setBusy = (value) => {
      self.busy = value;
    };

    const reset = () => {
      self.contracts = [];
      self.siteContracts = [];
      self.selectedProject = {};
      self.selectedContract = null;
      self.selectedContacts = [];
      self.selectedMessage = {};
      self.selectedDoc = {};
      self.error = "";
      self.sites = [];
      self.selectedTheme = {};
      self.licenses = 0;
      self.siteLogo = "";
      self.selectedSiteSpace = {};
      self.allMessages = [];
      self.busy = false;
    };

    return {
      getSaleContracts,
      setContracts,
      setSelectedContract,
      setSelectedContacts,
      setSelectedMessage,
      setSelectedDoc,
      getSiteSaleContracts,
      setSiteContracts,
      updateCustomerSites,
      updateCustomerUsers,
      getCustomerData,
      updateTenants,
      getCustomerSites,
      setCustomerSites,
      updateSiteActivation,
      updateTenantActivation,
      getSaleContract,
      getSaleContractDocs,
      getSaleContractMessages,
      setSelectedProject,
      setSelectedTheme,
      getProjectImage,
      sendResetPassword,
      sendLicenseUpdateEmail,
      sendFormEmail,
      resetPassword,
      setAdminPassword,
      updateMassge,
      updateNote,
      getTenants,
      getPortalData,
      setLicenses,
      getSaleContractNote,
      addSaleContractNote,
      getDepartments,
      getNoteTopics,
      getNoteStatuses,
      getSaleContractApplies,
      getBedekSiteSpaces,
      setSelectedSiteSpace,
      setError,
      setSiteLogo,
      setAllMessages,
      setBusy,
      reset,
      updateCustSiteActivation,
      updateSpaceActivation
    };
  })
  .views((self) => ({
    get isError() {
      return !!self.error;
    },
  }));

let sessionStorageTenant = JSON.parse(
  window.sessionStorage.getItem("coralis-tenants-storetenant")
);

const tenantsStore = TenantsStore.create({
  contracts: sessionStorageTenant?.contracts || [],
  // sites/siteContracts are not persisted (see `exclude` below) — Admin
  // refetches them after a reload.
  siteContracts: [],
  selectedProject: sessionStorageTenant?.selectedProject || {},
  selectedContract: sessionStorageTenant?.selectedContract || null,
  selectedMessage: sessionStorageTenant?.selectedMessage || {},
  // Not persisted (see `exclude` below) — an older snapshot may still carry a
  // multi-MB document, so don't read it back.
  selectedDoc: {},
  sites: [],
  error: "",
  selectedTheme: sessionStorageTenant?.selectedTheme || {},
  licenses: 0,
  siteLogo: sessionStorageTenant?.siteLogo || "",
  selectedSiteSpace: sessionStorageTenant?.selectedSiteSpace || {},
  selectedContacts: sessionStorageTenant?.selectedContacts || [],
  allMessages: sessionStorageTenant?.allMessages || [],
});

sessionStorageTenant = persist("coralis-tenants-storetenant", tenantsStore, {
  storage: window.sessionStorage,
  jsonify: true,
  // These exceed the sessionStorage quota, which made every later save fail
  // silently and left a stale snapshot for reloads: the full customer sites
  // tree, and selectedDoc — whose EXTFILENAME holds the PDF itself (~25MB).
  // Both are reloaded from the server anyway.
  exclude: ["sites", "siteContracts", "selectedDoc"],
  shouldPersist: () => {
    try {
      const raw = window.sessionStorage.getItem("coralis-tenants-user");
      if (!raw) return false;
      const data = JSON.parse(raw);
      return data?.isAuthenticated === true;
    } catch (e) {
      return false;
    }
  },
});

// A fresh load/reload means no get_cust_data call is in flight, so clear any
// `busy: true` that may have been rehydrated from the persisted snapshot —
// otherwise the blocking overlay could be stuck on at startup.
tenantsStore.setBusy(false);

export default tenantsStore;
